import React, { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import SubHeader from '../../layout/components/SubHeader';
import patientService from '../../../services/patient.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import PatientSearchForm2 from './PatientSearchForm2';
import { useNavigate } from 'react-router-dom';

// Lazy load components
const PatientList = lazy(() => import('./PatientList'));
const AllPatientsPage = lazy(() => import('./AllPatientsPage'));

const Loader = ({ message = 'Loading...' }) => (
    <div className="text-center py-5">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p className="text-muted">{message}</p>
    </div>
);

const PatientManagement = () => {
    const navigate = useNavigate();

    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    // Zustand global store variables
    const {
        patientSearch,
        setPatientSearch,
        clearPatientSearch
    } = useEyeconxStore();

    const {
        patients,
        searchTerm,
        searchColumn,
        pagination,
        pageNo,
        searchPerformed
    } = patientSearch;

    const [isLoading, setIsLoading] = useState(false);
    const [view, setView] = useState('search'); // local — for toggling "all patients" view
    const pageSize = 10;

    // Build query string
    const buildQueryString = (searchColumn, searchTerm = '') => {
        const params = new URLSearchParams();
        if (searchColumn && searchColumn.trim()) params.append('column', searchColumn.trim());
        if (searchTerm.trim()) params.append('value', searchTerm.trim());
        params.append('sortBy', 'firstName,lastName');
        return params.toString();
    };

    // Load Patients
    const loadPatients = useCallback(
        async (searchColumn = '', searchTerm = '', page = 0, showAll = false) => {
            setIsLoading(true);

            try {
                const query = buildQueryString(searchColumn, searchTerm);
                const result = await patientService.getAllPatients(
                    clinicId,
                    page,
                    pageSize,
                    query
                );

                const updatedData = {
                    patients: result.data.content || [],
                    pagination: {
                        totalPages: result.data.totalPages,
                        totalElements: result.data.totalElements,
                        size: result.data.size,
                        number: result.data.number
                    },
                    searchTerm,
                    searchColumn,
                    pageNo: page,
                    searchPerformed: true
                };

                setPatientSearch(updatedData);
                setView(showAll ? 'all' : 'search');
            } catch (error) {
                console.error('Error loading patients:', error);
                clearPatientSearch();
            } finally {
                setIsLoading(false);
            }
        },
        [clinicId, pageSize, clearPatientSearch, setPatientSearch]
    );

    // Search button pressed
    const handleSearch = (column, term, showAll = false) => {
        loadPatients(column, term, 0, showAll);
    };

    // Pagination
    const handlePageChange = (newPage) => {
        loadPatients(searchColumn, searchTerm, newPage, view === 'all');
    };

    // Show All Patients
    const handleShowAllPatients = () => {
        loadPatients('', '', 0, true);
    };

    // Back to default search (also clears store)
    const handleBackToSearch = () => {
        clearPatientSearch();
        setView('search');
    };

    // When opening patient detail
    const handlePatientSelect = (patient) => {
        navigate(`${patient.id}`);
    };

    // On initial mount, restore search results if they exist
    useEffect(() => {
        if (patientSearch.searchPerformed) {
            // Restore previous search view mode
            if (patientSearch.searchTerm || patientSearch.searchColumn) {
                setView('search');
            } else {
                setView('all');
            }
        }
    }, []);

    return (
        <>
            <SubHeader
                title="Patient Files"
                description="Search for patients or view all records"
            >
                <PatientSearchForm2
                    onSearch={handleSearch}
                    isLoading={isLoading}
                    searchTerm={searchTerm}
                    searchColumn={searchColumn}
                />
            </SubHeader>

            <div className="row justify-content-center py-4">
                <div className="col-12">
                    <Suspense fallback={<Loader message="Loading patient tools..." />}>
                        
                        {view === 'search' ? (
                            <>
                                {searchPerformed ? (
                                    <Suspense fallback={<Loader message="Loading search results..." />}>
                                        <PatientList
                                            patients={patients}
                                            isLoading={isLoading}
                                            pagination={pagination}
                                            currentPage={pageNo}
                                            onPageChange={handlePageChange}
                                            onPatientSelect={handlePatientSelect}
                                        />
                                    </Suspense>
                                ) : (
                                    <div className="card shadow-sm border-0">
                                        <div className="card-body text-center py-5">
                                            <i className="bi bi-person-lines-fill display-4 text-muted mb-3"></i>
                                            <h5 className="text-muted mb-3">Ready to Find Patients</h5>
                                            <p className="text-muted mb-4">
                                                Use the search above or click "Show All Patients".
                                            </p>
                                            <button
                                                className="btn btn-primary text-white"
                                                onClick={handleShowAllPatients}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" />
                                                        Loading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-people me-2"></i>
                                                        Show All Patients
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <Suspense fallback={<Loader message="Loading all patients..." />}>
                                <AllPatientsPage
                                    onBack={handleBackToSearch}
                                    isLoading={isLoading}
                                    patients={patients}
                                    pagination={pagination}
                                    currentPage={pageNo}
                                    onPageChange={handlePageChange}
                                    onPatientSelect={handlePatientSelect}
                                />
                            </Suspense>
                        )}

                    </Suspense>
                </div>
            </div>
        </>
    );
};

export default PatientManagement;
